import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import axios from 'axios'
import Breadcrumbs from '../components/Breadcrumbs'

type Ticket = {
  id: number
  subject: string
  status: string
  created_at: string
  updated_at: string
}

type Message = {
  id: number
  body: string
  created_at: string
  email: string | null
  name: string | null
}

export default function PortalTicket() {
  const { t } = useTranslation()
  const { id } = useParams()
  const ticketId = Number(id)

  const [ticket, setTicket] = useState<Ticket | null>(null)
  const [messages, setMessages] = useState<Message[]>([])
  const [loading, setLoading] = useState(true)
  const [reply, setReply] = useState('')
  const [sending, setSending] = useState(false)

  const load = async () => {
    const r = await axios.get(`/api/portal/tickets/${ticketId}`)
    setTicket(r.data?.ticket ?? null)
    setMessages(r.data?.messages ?? [])
  }

  useEffect(() => {
    let mounted = true
    ;(async () => {
      try {
        if (!Number.isFinite(ticketId)) return
        await load()
      } finally {
        if (mounted) setLoading(false)
      }
    })()
    return () => {
      mounted = false
    }
  }, [ticketId])

  const sendReply = async (e: React.FormEvent) => {
    e.preventDefault()
    setSending(true)
    try {
      await axios.post(`/api/portal/tickets/${ticketId}/messages`, { message: reply })
      setReply('')
      await load()
    } finally {
      setSending(false)
    }
  }

  return (
    <div className="min-h-screen pb-24">
      <Breadcrumbs items={[{ key: 'portal', path: '/portal' }]} />

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/portal" className="inline-flex items-center gap-2 text-primary font-medium hover:underline mb-6">
          <span className="material-icons text-lg">arrow_back</span>
          {t('portal.tickets')}
        </Link>

        {loading ? (
          <div className="bg-slate-200 dark:bg-slate-700 animate-pulse rounded-2xl h-64" />
        ) : !ticket ? (
          <div className="bg-white dark:bg-slate-800 rounded-2xl p-6 border border-slate-200 dark:border-slate-700">
            <p className="text-slate-600 dark:text-slate-400">Ticket introuvable.</p>
          </div>
        ) : (
          <>
            <div className="bg-white dark:bg-slate-800 rounded-2xl p-6 border border-slate-200 dark:border-slate-700 mb-6">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h1 className="text-2xl font-bold">#{ticket.id} — {ticket.subject}</h1>
                  <p className="text-xs text-slate-500 mt-1">{new Date(ticket.updated_at).toLocaleString()}</p>
                </div>
                <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200">
                  {ticket.status}
                </span>
              </div>
            </div>

            <div className="space-y-3 mb-6">
              {messages.map((m) => (
                <div key={m.id} className="bg-white dark:bg-slate-800 rounded-2xl p-5 border border-slate-200 dark:border-slate-700">
                  <div className="flex items-center justify-between gap-3 mb-2">
                    <p className="text-sm font-semibold">
                      {m.name || m.email || '—'}
                    </p>
                    <p className="text-xs text-slate-500">{new Date(m.created_at).toLocaleString()}</p>
                  </div>
                  <p className="text-slate-700 dark:text-slate-300 whitespace-pre-wrap">{m.body}</p>
                </div>
              ))}
            </div>

            <form onSubmit={sendReply} className="bg-white dark:bg-slate-800 rounded-2xl p-6 border border-slate-200 dark:border-slate-700">
              <label className="block text-sm font-semibold mb-2">{t('portal.reply')}</label>
              <textarea
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                required
                rows={4}
                className="w-full px-4 py-3 rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 focus:ring-2 focus:ring-primary focus:border-transparent transition-all resize-none"
              />
              <button
                type="submit"
                disabled={sending}
                className="mt-4 w-full px-6 py-3 bg-primary text-white rounded-xl font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50"
              >
                {sending ? t('portal.sending') : t('portal.sendReply')}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  )
}

